import type { Component } from 'vue'
import type { BottomSheet } from '@/types/app/bottom-sheet.model'
import type { Dialog } from '@/types/app/dialog.model'
import type { Drawer } from '@/types/app/drawer.model'
import type { ToastContent } from '@/types/app/toast.model'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { shallowRef } from 'vue'

const initBottomSheet: BottomSheet = {
  blockScroll: true,
  contentComponent: undefined,
  dismissable: true,
  entityData: undefined,
  entityId: undefined,
  fullHeight: false,
  modal: true,
  title: undefined,
  titleComponent: undefined,
  visible: false,
}

interface SetBottomSheetParams {
  contentComponent: Component
  entityData?: any
  fullHeight?: boolean
  title?: string
  titleComponent?: Component
}

interface SetDialogParams {
  contentComponent: Component
  entityData?: any
  title?: string
  titleComponent?: Component
}

const initDialog: Dialog = {
  blockScroll: true,
  contentComponent: undefined,
  dismissable: true,
  draggable: true,
  entityData: undefined,
  entityId: undefined,
  modal: true,
  position: 'center',
  title: undefined,
  titleComponent: undefined,
  visible: false,
}

const initDrawer: Drawer = {
  blockScroll: true,
  contentComponent: undefined,
  dismissable: false,
  formEntityContext: undefined,
  formEntityId: undefined,
  formLoaded: false,
  position: 'right',
  saveButtonLabel: undefined,
  title: undefined,
  visible: false,
}

const initToastContent: ToastContent = {
  closable: false,
  detail: '',
  life: 5000,
  severity: 'info',
  summary: '',
}

interface AppStore {
  asideCollapsable: boolean
  asideCollapsed: boolean
  bottomSheet: BottomSheet
  dialog: Dialog
  drawer: Drawer
  isMobile: boolean
  organisationTemperatureDisplayOffset: number
  toastContent: ToastContent
  toastVisible: boolean
}

export const useAppStore = defineStore('app', {
  state: (): AppStore => ({
    asideCollapsable: false,
    asideCollapsed: false,
    bottomSheet: initBottomSheet,
    dialog: initDialog,
    drawer: initDrawer,
    isMobile: false,
    organisationTemperatureDisplayOffset: 0,
    toastContent: initToastContent,
    toastVisible: false,
  }),

  getters: {
    isAsideCollapsable(state: AppStore): boolean {
      return state.asideCollapsable
    },
    bottomSheetEntityData(state: AppStore): any {
      return state.bottomSheet.entityData
    },
    dialogEntityData(state: AppStore): any {
      return state.dialog.entityData
    },
    drawerFormEntityId(state: AppStore): string | undefined {
      return state.drawer.formEntityId
    },
    drawerFormEntityContext(state: AppStore): any {
      return state.drawer.formEntityContext
    },
    drawerSaveButtonLabel(state: AppStore): string | undefined {
      return state.drawer.saveButtonLabel
    },
    isBottomSheetVisible(state: AppStore): boolean {
      return state.bottomSheet.visible
    },
    isDialogVisible(state: AppStore): boolean {
      return state.dialog.visible
    },
    isDrawerVisible(state: AppStore): boolean {
      return state.drawer.visible
    },
    sharedEntityData(state: AppStore): any {
      return state.bottomSheet.entityData || state.dialog.entityData
    },
  },

  actions: {
    // Aside collapsable
    setAsideCollapsable(collapsable: boolean) {
      this.asideCollapsable = collapsable
    },
    // Aside collapsed toggle
    toggleAsideCollapsed() {
      this.asideCollapsed = !this.asideCollapsed
    },
    // Bottom Sheet
    closeBottomSheet() {
      this.bottomSheet.contentComponent = undefined
      this.bottomSheet.entityId = undefined
      this.bottomSheet.title = undefined
      this.bottomSheet.titleComponent = undefined
      this.bottomSheet.visible = false
    },
    /**
     * Sets the bottom sheet and makes it visible.
     *
     * @param {object} bottomSheet - The bottom sheet options
     * @param {component} bottomSheet.contentComponent - Component that will be used for the content of the bottom sheet
     * @param {object} bottomSheet.entityData - Optional data object that will be available to the bottom sheet
     * @param {boolean} [bottomSheet.fullHeight] - Optional boolean setting (default is false)
     * @param {string} [bottomSheet.title] - Optional string value used to set the title
     * @param {component} [bottomSheet.titleComponent] - Optional Component that will be used for the title of the bottom sheet
     */
    setBottomSheet({
      contentComponent,
      entityData,
      fullHeight = false,
      title,
      titleComponent,
    }: SetBottomSheetParams): void {
      this.bottomSheet.contentComponent = shallowRef(contentComponent)
      this.bottomSheet.entityData = entityData
      this.bottomSheet.fullHeight = fullHeight
      this.bottomSheet.title = title
      if (titleComponent) {
        this.bottomSheet.title = ''
        this.bottomSheet.titleComponent = shallowRef(titleComponent)
      }
      this.bottomSheet.visible = true
    },
    // Dialog
    closeDialog() {
      this.dialog.contentComponent = undefined
      this.dialog.entityData = undefined
      this.dialog.title = undefined
      this.dialog.titleComponent = undefined
      this.dialog.visible = false
    },
    /**
     * Sets the dialog properties and makes it visible.
     *
     * @param {object} dialog - The dialog options
     * @param {component} dialog.contentComponent - The Vue component to be displayed in the dialog.
     * @param {object} [dialog.entityData] - Optional data associated with the dialog entity.
     * @param {string} [dialog.title] - Optional title for the dialog.
     * @param {component} [dialog.titleComponent] - Optional Vue component for the dialog title.
     */
    setDialog({ contentComponent, entityData, title, titleComponent }: SetDialogParams) {
      this.dialog.contentComponent = shallowRef(contentComponent)
      this.dialog.entityData = entityData
      this.dialog.title = title
      if (titleComponent) {
        this.dialog.titleComponent = shallowRef(titleComponent)
      }
      this.dialog.visible = true
    },
    // Is Mobile
    setIsMobile(isMobile: boolean) {
      this.isMobile = isMobile
    },
    // Organisation Temperature Display Offset
    setOrganisationTemperatureDisplayOffset(amount: number) {
      this.organisationTemperatureDisplayOffset = amount ?? 0
    },
    // Drawer
    closeDrawer() {
      this.drawer.visible = false
      this.drawer.contentComponent = undefined
      this.drawer.formEntityContext = undefined
      this.drawer.formEntityId = undefined
      this.drawer.formLoaded = false
      this.drawer.saveButtonLabel = ''
      this.drawer.title = undefined
    },
    openDrawer() {
      this.drawer.visible = true
    },
    setDrawerContentComponent(component: Component) {
      this.drawer.contentComponent = shallowRef(component)
    },
    setDrawerFormContext(context: any) {
      this.drawer.formEntityContext = context
    },
    setDrawerFormEntityId(id: string) {
      this.drawer.formEntityId = id
    },
    setDrawerFormContextPropertyWithValue({ property, value }: { property: string, value: any }) {
      if (this.drawer.formEntityContext) {
        this.drawer.formEntityContext = {
          ...this.drawer.formEntityContext,
          [property]: value,
        }
      }
    },
    setDrawerFormLoaded(loaded: boolean) {
      this.drawer.formLoaded = loaded
    },
    setDrawerSaveButtonLabel(label?: string) {
      this.drawer.saveButtonLabel = label
    },
    setDrawerTitle(title: string) {
      this.drawer.title = title
    },
    // Toast
    resetToast() {
      this.toastContent = initToastContent
      this.toastVisible = false
    },
    setToastContent(content: ToastContent) {
      this.toastContent = content
    },
    showToast() {
      this.toastVisible = true
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot))
}
